/**
 * GlobalSearch — semantic (RAG) search box for the active TopBar shell.
 *
 * Reuses the knowledge-service semantic search already built in
 * `copilot.service.ts` (`useKnowledgeSearch` → POST /api/v1/knowledge/entities/search).
 * Renders ranked results grouped by entity_type with a relevance score;
 * the query is debounced (~250ms). Ctrl/Cmd+K focuses the input,
 * Escape / click-away closes the dropdown, clicking a result navigates
 * to its URL.
 *
 * Graceful by design: if the endpoint errors or returns nothing, an empty
 * state is shown — the component never throws.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Search,
  X,
  Loader2,
  ArrowRight,
  Sparkles,
  AlertTriangle,
  Shield,
  FileText,
  ClipboardCheck,
  Scale,
  Database,
  BookOpen,
} from 'lucide-react'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { ScrollArea } from '@/components/ui/scroll-area'
import { cn } from '@/lib/utils'
import { useKnowledgeSearch, type KnowledgeEntity } from '@/services/copilot.service'

// Per-entity-type icon + label so the grouped dropdown is scannable.
const TYPE_CONFIG: Record<string, { icon: typeof Search; label: string; color: string }> = {
  risk: { icon: AlertTriangle, label: 'Risque', color: 'text-red-500' },
  control: { icon: Shield, label: 'Contrôle', color: 'text-blue-500' },
  policy: { icon: FileText, label: 'Politique', color: 'text-purple-500' },
  assessment: { icon: ClipboardCheck, label: 'Évaluation', color: 'text-green-500' },
  compliance: { icon: Scale, label: 'Conformité', color: 'text-amber-500' },
  obligation: { icon: ClipboardCheck, label: 'Obligation', color: 'text-teal-500' },
  evidence: { icon: Database, label: 'Preuve', color: 'text-orange-500' },
  finding: { icon: Search, label: 'Constat', color: 'text-amber-500' },
  incident: { icon: AlertTriangle, label: 'Incident', color: 'text-rose-500' },
  framework: { icon: BookOpen, label: 'Référentiel', color: 'text-indigo-500' },
  vendor: { icon: Database, label: 'Fournisseur', color: 'text-cyan-500' },
}

function typeMeta(type: string) {
  return (
    TYPE_CONFIG[type] ?? {
      icon: Sparkles,
      label: type ? type.charAt(0).toUpperCase() + type.slice(1) : 'Résultat',
      color: 'text-muted-foreground',
    }
  )
}

export function GlobalSearch({ className }: { className?: string }) {
  const navigate = useNavigate()
  const containerRef = useRef<HTMLDivElement>(null)

  const [query, setQuery] = useState('')
  const [debounced, setDebounced] = useState('')
  const [isOpen, setIsOpen] = useState(false)

  // Debounce the query (~250ms) so we don't fire a request per keystroke.
  useEffect(() => {
    const t = setTimeout(() => setDebounced(query.trim()), 250)
    return () => clearTimeout(t)
  }, [query])

  // Only fetch once the box is open (and query >= 2 chars — enforced by the hook).
  const { data, isFetching, isError } = useKnowledgeSearch(debounced, undefined, isOpen)
  const results = data ?? []

  // Group ranked results by entity_type, preserving relevance order per group.
  const groups = useMemo(() => {
    const map = new Map<string, KnowledgeEntity[]>()
    for (const r of results) {
      const arr = map.get(r.entity_type) ?? []
      arr.push(r)
      map.set(r.entity_type, arr)
    }
    return Array.from(map.entries())
  }, [results])

  const showDropdown = isOpen && debounced.length >= 2

  // Close on outside click.
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  // Escape closes the results dropdown. NOTE: Ctrl/⌘K is owned by the command
  // palette (command-palette.tsx); this input is reached via the `/` shortcut
  // (it carries data-search-input), so we must NOT also grab Ctrl+K here.
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsOpen(false)
    }
    document.addEventListener('keydown', handler)
    return () => document.removeEventListener('keydown', handler)
  }, [])

  const handleSelect = useCallback(
    (r: KnowledgeEntity) => {
      setIsOpen(false)
      setQuery('')
      if (r.url) navigate(r.url)
    },
    [navigate],
  )

  return (
    <div ref={containerRef} className={cn('relative', className)}>
      <div className="relative">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
        <Input
          data-search-input
          value={query}
          onChange={(e) => {
            setQuery(e.target.value)
            setIsOpen(true)
          }}
          onFocus={() => query.trim().length >= 2 && setIsOpen(true)}
          placeholder="Recherche sémantique…  ( / )"
          aria-label="Recherche sémantique"
          autoComplete="off"
          className="h-9 pl-9 pr-8 text-[13px] bg-background"
        />
        {query ? (
          <button
            type="button"
            onClick={() => {
              setQuery('')
              setIsOpen(false)
            }}
            className="absolute right-2.5 top-1/2 -translate-y-1/2"
            aria-label="Effacer la recherche"
          >
            <X className="h-3.5 w-3.5 text-muted-foreground hover:text-foreground" />
          </button>
        ) : null}
      </div>

      {showDropdown ? (
        <div className="absolute left-0 top-full z-50 mt-1.5 w-[26rem] max-w-[80vw] overflow-hidden rounded-md border bg-popover text-popover-foreground shadow-lg">
          <ScrollArea className="max-h-[60vh]">
            {isFetching && results.length === 0 ? (
              <div className="flex items-center justify-center gap-2 py-8 text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
                <span className="text-sm">Recherche…</span>
              </div>
            ) : results.length === 0 ? (
              <div className="py-8 text-center text-sm text-muted-foreground">
                {isError
                  ? 'Recherche indisponible pour le moment.'
                  : `Aucun résultat pour « ${debounced} »`}
              </div>
            ) : (
              <div className="py-1">
                <div className="flex items-center gap-1.5 px-3 py-1.5 text-[11px] text-muted-foreground">
                  <Sparkles className="h-3 w-3 text-primary" />
                  Recherche sémantique
                </div>
                {groups.map(([type, items]) => {
                  const meta = typeMeta(type)
                  const Icon = meta.icon
                  return (
                    <div key={type} className="py-1">
                      <p className="px-3 pb-1 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                        {meta.label}
                      </p>
                      {items.map((r, i) => (
                        <button
                          key={`${type}-${r.entity_id}-${i}`}
                          type="button"
                          disabled={!r.url}
                          onClick={() => handleSelect(r)}
                          className={cn(
                            'flex w-full items-start gap-3 px-3 py-2.5 text-left transition-colors',
                            r.url ? 'cursor-pointer hover:bg-accent' : 'cursor-default',
                          )}
                        >
                          <Icon className={cn('mt-0.5 h-4 w-4 shrink-0', meta.color)} />
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2">
                              <span className="truncate text-sm font-medium">{r.title}</span>
                              {typeof r.score === 'number' ? (
                                <Badge variant="outline" className="shrink-0 text-[10px] tabular-nums">
                                  {Math.round(r.score * 100)}%
                                </Badge>
                              ) : null}
                            </div>
                            {r.snippet ? (
                              <p className="mt-0.5 truncate text-xs text-muted-foreground">
                                {r.snippet}
                              </p>
                            ) : null}
                          </div>
                          {r.url ? (
                            <ArrowRight className="mt-1 h-3 w-3 shrink-0 text-muted-foreground" />
                          ) : null}
                        </button>
                      ))}
                    </div>
                  )
                })}
              </div>
            )}
          </ScrollArea>
        </div>
      ) : null}
    </div>
  )
}

export default GlobalSearch
